"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";
import Image from "next/image";
import * as mLazy from "motion/react-m";
import { AnimatePresence } from "motion/react";
import { useTranslations } from "next-intl";
import { GridItem, TRANSITIONS } from "./types";
import { GenerateScene } from "./scenes/GenerateVideoFromImage";
import { LipsyncScene } from "./scenes/LipSync";
import { UpscaleScene } from "./scenes/Upscale";
import { ImageGenerateScene } from "./scenes/ImageGenerate";
import { ImageUpscaleScene } from "./scenes/ImageUpscale";
import { VideoPlayer } from "./VideoPlayer";

type ActiveScene = "generate" | "lipsync" | "upscale" | "imageGenerate" | "imageUpscale";

type SceneGroup = "image" | "video";

const SCENE_GROUPS: Record<SceneGroup, ActiveScene[]> = {
  image: ["imageGenerate", "imageUpscale"],
  video: ["generate", "lipsync", "upscale"],
};

function getActiveScenes(sceneGroup: SceneGroup = "video"): ActiveScene[] {
  return SCENE_GROUPS[sceneGroup] ?? SCENE_GROUPS.video;
}

export interface HeroDemoProps {
  className?: string;
  sceneGroup?: SceneGroup;
}

function GridItemPlaceholder({
  type,
  item,
  onVideoEnded,
  shouldStartPlayback,
}: {
  type: "original" | "result" | "empty";
  item?: GridItem | null;
  onVideoEnded?: () => void;
  shouldStartPlayback?: boolean;
}) {
  const t = useTranslations("ClientKeys.VideoImageGenerationHero");

  const handleVideoEnded = () => {
    if (onVideoEnded && type === "result") {
      onVideoEnded();
    }
  };

  // For image results, trigger scene transition after a display duration
  useEffect(() => {
    if (type !== "result" || item?.status !== "complete" || !item.isImageResult || !onVideoEnded) return;
    const timer = setTimeout(onVideoEnded, 3000);
    return () => clearTimeout(timer);
  }, [type, item?.status, item?.isImageResult, onVideoEnded]);

  return (
    <div
      className={clsx(
        "tw-aspect-[3/2] tw-rounded-2xl tw-relative tw-overflow-hidden",
        type !== "empty" && "bg-dark-new",
        type === "empty" && "md:tw-block hidden bg-gradient-to-b from-[#F5F3F1] to-transparent",
        !item && type !== "empty" && "tw-opacity-60",
      )}
    >
      <AnimatePresence mode='wait'>
        {item?.status === "complete" && (
          <mLazy.div
            key={`${item.type}-${item.media || item.imageUrl}`}
            className='tw-absolute tw-inset-0'
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={TRANSITIONS.slow}
          >
            {item.imageUrl ? (
              <Image
                src={item.imageUrl}
                alt={t("original_content_alt")}
                fill
                className='tw-object-cover tw-rounded-2xl'
                sizes='(max-width: 768px) 100vw, 50vw'
              />
            ) : (
              <VideoPlayer
                src={item.media}
                autoPlay={type === "result"}
                hasAudio={item.hasAudio}
                shouldPlay={type === "original" ? shouldStartPlayback : undefined}
                onEnded={handleVideoEnded}
              />
            )}
          </mLazy.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode='wait'>
        {item?.status === "generating" && item.label && (
          <mLazy.div
            key={`${item.type}-generating`}
            className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={TRANSITIONS.medium}
          >
            <span className='tw-animate-pulse f-paragraph-03 text-light'>{item.label}</span>
          </mLazy.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ImageVideoHero({ className, sceneGroup }: HeroDemoProps) {
  const scenes = getActiveScenes(sceneGroup);

  const [originalItem, setOriginalItem] = useState<GridItem | null>(null);
  const [resultItem, setResultItem] = useState<GridItem | null>(null);
  const [activeScene, setActiveScene] = useState<ActiveScene>(scenes[0]);
  const [isPromptBoxVisible, setIsPromptBoxVisible] = useState(true);

  const isSinglePanelScene = activeScene === "imageGenerate";

  const handleGenerationStarted = (mediaUrl: string, mediaType: "image" | "video", label: string) => {
    if (isSinglePanelScene) {
      setOriginalItem({
        type: activeScene,
        media: "",
        status: "generating",
        label,
      });
    } else {
      setOriginalItem({
        type: activeScene,
        media: mediaType === "video" ? mediaUrl : "",
        status: "complete",
        imageUrl: mediaType === "image" ? mediaUrl : undefined,
      });
      setResultItem({
        type: activeScene,
        media: "",
        status: "generating",
        label,
      });
    }
  };

  const handleSceneComplete = (result: GridItem) => {
    if (isSinglePanelScene) {
      setOriginalItem(result);
    } else {
      setResultItem(result);
    }
  };

  const handleResultVideoEnded = () => {
    const currentIndex = scenes.indexOf(activeScene);
    const nextScene = scenes[(currentIndex + 1) % scenes.length];
    const isLoopingBack = currentIndex === scenes.length - 1;

    if (isLoopingBack) {
      setIsPromptBoxVisible(false);
    }

    // Wait for fade-out if looping, then switch scene
    setTimeout(
      () => {
        setOriginalItem(null);
        setResultItem(null);
        setActiveScene(nextScene);

        if (isLoopingBack) {
          // Fade prompt box back in after scene switch
          setTimeout(() => setIsPromptBoxVisible(true), 100);
        }
      },
      isLoopingBack ? 800 : 0,
    );
  };

  return (
    <div className={clsx("tw-relative tw-w-full tw-max-w-2xl tw-mx-auto", className)}>
      <div className='tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3'>
        <GridItemPlaceholder
          type={isSinglePanelScene ? "result" : "original"}
          item={originalItem}
          onVideoEnded={isSinglePanelScene ? handleResultVideoEnded : undefined}
          shouldStartPlayback={!!originalItem?.media && !originalItem?.imageUrl && resultItem?.status === "complete"}
        />

        <GridItemPlaceholder type='result' item={resultItem} onVideoEnded={handleResultVideoEnded} />
      </div>

      <div className='md:tw-grid md:tw-grid-cols-2 tw-gap-3 tw-relative tw-min-h-48 md:tw-min-h-40'>
        <mLazy.div
          className='md:tw-absolute md:tw-inset-0'
          animate={{
            opacity: isPromptBoxVisible ? 1 : 0,
          }}
          transition={TRANSITIONS.fast}
        >
          <div
            className='tw-p-3 tw-w-full tw-max-w-xl tw-mx-auto md:tw-absolute md:tw-top-1/2 md:-tw-translate-y-1/2 md:tw-left-1/2 md:-tw-translate-x-1/2 tw-z-10 bg-default'
            style={{
              boxShadow:
                "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 2px 0 rgba(0, 0, 0, 0.04), 0 2px 2px 0 rgba(0, 0, 0, 0.04)",
              borderRadius: "30px",
            }}
          >
            {activeScene === "generate" && (
              <GenerateScene onGenerationStarted={handleGenerationStarted} onComplete={handleSceneComplete} />
            )}
            {activeScene === "lipsync" && (
              <LipsyncScene onGenerationStarted={handleGenerationStarted} onComplete={handleSceneComplete} />
            )}
            {activeScene === "upscale" && (
              <UpscaleScene onGenerationStarted={handleGenerationStarted} onComplete={handleSceneComplete} />
            )}
            {activeScene === "imageGenerate" && (
              <ImageGenerateScene onGenerationStarted={handleGenerationStarted} onComplete={handleSceneComplete} />
            )}
            {activeScene === "imageUpscale" && (
              <ImageUpscaleScene onGenerationStarted={handleGenerationStarted} onComplete={handleSceneComplete} />
            )}
          </div>
        </mLazy.div>
        <GridItemPlaceholder type='empty' item={null} />
        <GridItemPlaceholder type='empty' item={null} />
      </div>
    </div>
  );
}
